import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import JoditEditor from "jodit-react";
import { ServerUrl } from '../../../../../api/ServerUrl';
import { ProtectAPi } from '../../../../../api/ProtectAPi';
import swal from 'sweetalert';
import BackButton from '../../../../tools/BackButton';

const PublicationEdit = () => {
    const { http } = ProtectAPi();
    const { id } = useParams();
    const navigate = useNavigate();
    const editor = useRef(null);

    const [loader, setLoader] = useState('off');
    const [layoutTitle, setLayoutTitle] = useState('');
    const [layoutSlug, setLayoutSlug] = useState('');
    const [layoutDescription, setLayoutDescription] = useState('');
    const [layoutAltText, setLayoutAltText] = useState('');
    const [layoutImage, setLayoutImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const [layoutImageError, setLayoutImageError] = useState('');
    const [titleError, setTitleError] = useState('');
    const [slugError, setSlugError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [layoutAltTextError, setLayoutAltTextError] = useState('');

    const httpCPage = ServerUrl;

    useEffect(() => {
        http.get(`/get-single-publication/${id}`)
            .then(res => {
                setLayoutTitle(res.data.title);
                setLayoutSlug(res.data.slug);
                setLayoutDescription(res.data.description);
                setLayoutAltText(res.data.alt || '');
                if (res.data.image) {
                    const layoutImageShow = `${httpCPage}/backend/img/publications/${res.data.image}`;
                    setImagePreview(layoutImageShow);
                }
            })
            .catch(error => {
                console.error('Error fetching publication:', error);
            });
    }, [id]);

    const submitForm = () => {
        setLoader('on');
        setLayoutImageError('');
        setTitleError('');
        setSlugError('');
        setDescriptionError('');
        setLayoutAltTextError('');

        const formData = new FormData();
        formData.append('title', layoutTitle);
        formData.append('slug', layoutSlug);
        formData.append('description', layoutDescription);
        formData.append('alt', layoutAltText);
        if (layoutImage) {
            formData.append('image', layoutImage);
        }

        http.post(`/update-publication/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        }).then(res => {
            if (res.data.status === '422') {
                const errors = res.data.error;
                setLoader('off');
                setLayoutImageError(errors.image ? errors.image[0] : '');
                setTitleError(errors.title ? errors.title[0] : '');
                setSlugError(errors.slug ? errors.slug[0] : '');
                setDescriptionError(errors.description ? errors.description[0] : '');
                setLayoutAltTextError(errors.alt ? errors.alt[0] : '');
            } else {
                setLoader('off');
                swal('Success', res.data.msg, 'success');
                navigate('/admin/pages/home/publication-list');
            }
        }).catch(error => {
            setLoader('off');
            console.error('Error during update:', error);
        });
    };

    const handleImageChange = (e) => {
        const selectedImage = e.target.files[0];
        setLayoutImage(selectedImage);
        const previewURL = URL.createObjectURL(selectedImage);
        setImagePreview(previewURL);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'layoutTitle') setLayoutTitle(value);
        else if (name === 'layoutSlug') setLayoutSlug(value);
        else if (name === 'layoutAltText') setLayoutAltText(value);
    };

    const editorConfig = useMemo(() => ({
        readonly: false,
        height: 300,
        placeholder: 'Start typing...',
        uploader: {
            insertImageAsBase64URI: true
        }
    }), []);

    return (
        <div className="main-content">
            <section className="section">
                <div className="section-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="card card-primary">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                        <h5>Edit Publication</h5>
                                        <BackButton />
                                    </div>

                                    <div className="card-body pb-0">
                                        <div className="row">
                                            {/* Image Upload */}
                                            <div className="col-md-4 mt-4">
                                                <label>Publication Image</label>
                                                <div className="logo-box">
                                                    <div className="logo">
                                                        <label htmlFor="layoutImage" className="w-100">
                                                            <div className="image-box border p-2 rounded shadow-sm d-flex justify-content-center align-items-center" style={{ height: '220px', backgroundColor: '#f9f9f9' }}>
                                                                {imagePreview ? (
                                                                    <img src={imagePreview} className="img-fluid rounded" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} alt="Preview" />
                                                                ) : (
                                                                    <img src="https://w7.pngwing.com/pngs/159/705/png-transparent-computer-computer-trademark-computer-logo.png" className="img-fluid rounded" alt="Default" />
                                                                )}
                                                            </div>
                                                            <input type="file" id="layoutImage" className="form-control d-none" onChange={handleImageChange} />
                                                            <span className="btn btn-primary mt-2 w-100">Upload Publication Image</span>
                                                            <span className="text-danger">{layoutImageError}</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Alt Text */}
                                            <div className="col-md-12 mt-4">
                                                <label>Alt Text for Image (SEO & Accessibility)</label>
                                                <input
                                                    type="text"
                                                    name="layoutAltText"
                                                    value={layoutAltText}
                                                    className="form-control"
                                                    placeholder="Enter alt text for the image"
                                                    onChange={handleInputChange}
                                                />
                                                <span className="text-danger">{layoutAltTextError}</span>
                                            </div>

                                            {/* Title */}
                                            <div className="col-md-12 mt-3">
                                                <div className="form-group">
                                                    <label>Title</label>
                                                    <input type="text" name="layoutTitle" value={layoutTitle} className="form-control" placeholder="Enter Title" onChange={handleInputChange} />
                                                    <span className="text-danger">{titleError}</span>
                                                </div>
                                            </div>

                                            {/* Slug */}
                                            <div className="col-md-12 mt-2">
                                                <div className="form-group">
                                                    <label>Slug (Optional)</label>
                                                    <input type="text" name="layoutSlug" value={layoutSlug} className="form-control" placeholder="Enter Slug" onChange={handleInputChange} />
                                                    <span className="text-danger">{slugError}</span>
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <div className="col-md-12 mt-4">
                                                <label>Description</label>
                                                <div style={{ border: '1px solid #ced4da', borderRadius: '0.25rem', overflow: 'hidden' }}>
                                                    <JoditEditor
                                                        ref={editor}
                                                        value={layoutDescription}
                                                        config={{
                                                            readonly: false,
                                                            height: 400,
                                                            placeholder: 'Start typing...',
                                                            uploader: {
                                                                insertImageAsBase64URI: true
                                                            }
                                                        }}
                                                        tabIndex={1}
                                                        onBlur={newContent => setLayoutDescription(newContent)}
                                                        onChange={() => { }}
                                                    />
                                                </div>
                                                <span className="text-danger">{descriptionError}</span>
                                            </div>

                                            {/* Submit Button */}
                                            <div className="col-md-12 mt-5 mb-3">
                                                {loader === 'off' ? (
                                                    <button type="submit" onClick={submitForm} className="btn btn-lg btn-primary">Update</button>
                                                ) : (
                                                    <div className="spinner-border text-dark" role="status"></div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PublicationEdit;
